/**
 * DOCX Exporter - 基于 AST 解析的 Word 导出工具
 * 参考 markdown-viewer-extension 的实现方案
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  ExternalHyperlink,
  InternalHyperlink,
  Bookmark,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
  TableLayoutType,
  PageBreak,
  PageOrientation,
  convertInchesToTwip,
  ITableCellOptions,
  IParagraphOptions,
  IRunOptions,
} from 'docx';

// ============ 单位转换 ============

/**
 * pt 转 half-points (Word 字号单位)
 * 1pt = 2 half-points
 */
export function ptToHalfPt(pt: number): number {
  return Math.round(pt * 2);
}

/**
 * pt 转 twips (Word 间距单位)
 * 1pt = 20 twips
 */
export function ptToTwips(pt: number): number {
  return Math.round(pt * 20);
}

/**
 * px 转 pt
 * 1pt = 4/3 px (96 DPI)
 */
export function pxToPt(px: number): number {
  return px * 0.75;
}

/**
 * 边框宽度转换 (pt 转 eighths)
 * 1pt = 8 eighths
 */
export function ptToEighths(pt: number): number {
  return Math.round(pt * 8);
}

// ============ 颜色处理 ============

/**
 * 移除颜色值的 # 前缀
 */
export function normalizeColor(color: string): string {
  return color.replace('#', '');
}

// ============ 字体配置 ============

/**
 * DOCX 字体配置 - 支持中英文分离
 * Word 对中文和西文使用分离的字体设置
 */
export interface DocxFontConfig {
  ascii: string; // 西文字体 (英文、数字)
  eastAsia: string; // 东亚字体 (中日韩)
  hAnsi: string; // 高位 ANSI 字符
  cs: string; // 复杂脚本字体
}

// 默认字体配置 - 与 markdown-viewer-extension 一致
export const defaultFontConfig: DocxFontConfig = {
  ascii: 'Times New Roman',
  eastAsia: 'SimSun', // 宋体
  hAnsi: 'Times New Roman',
  cs: 'Times New Roman',
};

// ============ 样式配置 ============

export interface DocxStyles {
  // 基础字体
  baseFontSize: number; // pt
  baseFontFamily: string;
  baseFontConfig: DocxFontConfig; // 完整字体配置
  lineHeight: number; // 倍数，如 1.5

  // 标题样式
  headings: {
    [key: string]: {
      fontSize: number;
      bold: boolean;
      spacingBefore: number; // pt
      spacingAfter: number; // pt
    };
  };

  // 表格样式
  table: {
    borderColor: string;
    borderWidth: number; // pt
    headerBackground: string;
    headerBold: boolean;
    cellPadding: number; // pt
    zebra: {
      enabled: boolean;
      oddBackground: string;
      evenBackground: string;
    };
  };

  // 代码块样式
  code: {
    fontFamily: string;
    fontSize: number;
    background: string;
    borderColor: string;
  };

  // 引用块样式
  blockquote: {
    borderColor: string;
    borderWidth: number;
    paddingLeft: number;
  };
}

// 默认样式配置 - 与 markdown-viewer-extension 一致
export const defaultStyles: DocxStyles = {
  baseFontSize: 12,
  baseFontFamily: 'Times New Roman',
  baseFontConfig: defaultFontConfig,
  lineHeight: 1.5,

  headings: {
    // 与 markdown-viewer-extension 的 styles.xml 一致
    // h1 居中，其他左对齐，无颜色（黑色）
    h1: { fontSize: 22, bold: true, spacingBefore: 3, spacingAfter: 3 }, // before=60, after=60 twips / 20
    h2: { fontSize: 18, bold: true, spacingBefore: 11, spacingAfter: 1 }, // before=220, after=20 twips / 20
    h3: { fontSize: 16, bold: true, spacingBefore: 10, spacingAfter: 0.5 }, // before=200, after=10 twips / 20
    h4: { fontSize: 14, bold: true, spacingBefore: 9, spacingAfter: 0 }, // before=180, after=0
    h5: { fontSize: 13, bold: true, spacingBefore: 8, spacingAfter: 0 }, // before=160, after=0
    h6: { fontSize: 12, bold: true, spacingBefore: 7, spacingAfter: 0 }, // before=140, after=0
  },

  table: {
    borderColor: 'CCCCCC',
    borderWidth: 1,
    headerBackground: 'F0F0F0',
    headerBold: true,
    cellPadding: 5,
    zebra: {
      enabled: true,
      oddBackground: 'FFFFFF',
      evenBackground: 'F9F9F9',
    },
  },

  code: {
    fontFamily: 'Consolas',
    fontSize: 10,
    background: 'F6F8FA',
    borderColor: 'E1E4E8',
  },

  blockquote: {
    borderColor: 'DFE2E5',
    borderWidth: 3,
    paddingLeft: 10,
  },
};

// ============ AST 节点类型 ============

interface MdastNode {
  type: string;
  children?: MdastNode[];
  value?: string;
  depth?: number;
  url?: string;
  title?: string;
  lang?: string;
  meta?: string;
  ordered?: boolean;
  start?: number;
  align?: (string | null)[];
}

// ============ 转换器类 ============

export class DocxExporter {
  private styles: DocxStyles;
  /** 书签 ID 缓存，确保相同文本生成相同的书签 ID */
  private bookmarkIdCache = new Map<string, string>();

  constructor(styles: Partial<DocxStyles> = {}) {
    this.styles = { ...defaultStyles, ...styles };
  }

  /** 导出配置 */
  private exportOptions: ExportOptions = { treatHrAsPageBreak: true };

  /**
   * 将 Markdown 转换为 DOCX Blob
   */
  async exportToBlob(markdown: string, options?: ExportOptions): Promise<Blob> {
    // 重置书签 ID 缓存
    this.bookmarkIdCache.clear();

    // 保存导出配置
    this.exportOptions = { treatHrAsPageBreak: true, ...options };

    // 解析 Markdown 为 AST
    const processor = unified().use(remarkParse).use(remarkGfm);
    const ast = processor.parse(markdown);

    // 转换 AST 为 docx 元素
    const children = await this.convertNodes(ast.children as MdastNode[]);

    // 根据方向设置页面尺寸
    // A4 尺寸: 210mm x 297mm = 8.27in x 11.69in
    const isLandscape = this.exportOptions.orientation === 'landscape';
    const pageOrientation = isLandscape
      ? PageOrientation.LANDSCAPE
      : PageOrientation.PORTRAIT;

    // 创建文档 - 样式与 markdown-viewer-extension 一致
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: this.styles.baseFontConfig, // 中英文分离字体配置
              size: ptToHalfPt(this.styles.baseFontSize),
            },
            paragraph: {
              spacing: {
                line: Math.round(this.styles.lineHeight * 240), // 360 = 1.5倍行距
                before: 160, // 与 markdown-viewer-extension 一致
                after: 40, // 与 markdown-viewer-extension 一致
              },
            },
          },
        },
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                // 自定义页边距：上下左右均为 2.54cm（1英寸）
                top: convertInchesToTwip(1),
                right: convertInchesToTwip(1),
                bottom: convertInchesToTwip(1),
                left: convertInchesToTwip(1),
              },
              size: {
                orientation: pageOrientation,
              },
            },
          },
          children,
        },
      ],
    });

    return await Packer.toBlob(doc);
  }

  /**
   * 转换 AST 节点数组
   */
  private async convertNodes(
    nodes: MdastNode[]
  ): Promise<(Paragraph | Table)[]> {
    const result: (Paragraph | Table)[] = [];
    let hasRealContent = false;

    for (const node of nodes) {
      // 跳过文档开头的分页符（第一页不需要分页）
      if (node.type === 'thematicBreak' && !hasRealContent) {
        continue;
      }

      const converted = await this.convertNode(node);
      if (converted) {
        if (Array.isArray(converted)) {
          result.push(...converted);
        } else {
          result.push(converted);
        }
        // 标记已有真实内容（分页符不算真实内容）
        if (node.type !== 'thematicBreak') {
          hasRealContent = true;
        }
      }
    }

    return result;
  }

  /**
   * 转换单个 AST 节点
   */
  private async convertNode(
    node: MdastNode
  ): Promise<Paragraph | Table | (Paragraph | Table)[] | null> {
    switch (node.type) {
      case 'heading':
        return this.convertHeading(node);
      case 'paragraph':
        return this.convertParagraph(node);
      case 'text':
        return new Paragraph({ children: [new TextRun(node.value || '')] });
      case 'code':
        return this.convertCodeBlock(node);
      case 'blockquote':
        return this.convertBlockquote(node);
      case 'list':
        return this.convertList(node);
      case 'listItem':
        return this.convertListItem(node);
      case 'table':
        return this.convertTable(node);
      case 'thematicBreak':
        return this.convertThematicBreak();
      case 'html':
        // 解析 HTML 锚点标签 <a id="xxx"></a> 或 <a name="xxx"></a>
        return this.convertHtmlAnchor(node);
      default:
        // 对于未知类型，尝试处理其子节点
        if (node.children) {
          return this.convertNodes(node.children);
        }
        return null;
    }
  }

  /**
   * 转换 HTML 锚点标签为 Word 书签
   * 支持 <a id="xxx"></a> 和 <a name="xxx"></a> 格式
   */
  private convertHtmlAnchor(node: MdastNode): Paragraph | null {
    const html = node.value || '';

    // 匹配 <a id="xxx"> 或 <a name="xxx"> 格式
    const idMatch = html.match(
      /<a\s+(?:[^>]*?\s+)?id\s*=\s*["']([^"']+)["'][^>]*>/i
    );
    const nameMatch = html.match(
      /<a\s+(?:[^>]*?\s+)?name\s*=\s*["']([^"']+)["'][^>]*>/i
    );

    const anchorId = idMatch?.[1] || nameMatch?.[1];

    if (anchorId) {
      // 使用相同的书签 ID 生成逻辑，确保与链接匹配
      const bookmarkId = this.generateBookmarkId(anchorId);

      // 创建一个包含空书签的段落
      return new Paragraph({
        children: [
          new Bookmark({
            id: bookmarkId,
            children: [new TextRun('')], // 空内容，仅作为锚点
          }),
        ],
        spacing: { before: 0, after: 0 },
      });
    }

    // 不是锚点标签，忽略
    return null;
  }

  /**
   * 从节点中提取纯文本（用于生成书签 ID）
   */
  private extractTextFromNode(node: MdastNode): string {
    if (node.value) {
      return node.value;
    }
    if (node.children) {
      return node.children
        .map(child => this.extractTextFromNode(child))
        .join('');
    }
    return '';
  }

  /**
   * 生成简单的文本哈希值
   */
  private hashText(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * 生成 Word 书签 ID
   * Word 书签规则：必须以字母开头，只能包含字母、数字、下划线，最多 40 字符
   * 使用哈希值确保不同文本生成不同 ID，使用缓存确保相同文本生成相同 ID
   */
  private generateBookmarkId(text: string): string {
    // 检查缓存，确保相同文本返回相同的书签 ID
    if (this.bookmarkIdCache.has(text)) {
      return this.bookmarkIdCache.get(text)!;
    }

    // 生成文本哈希确保唯一性
    const hash = this.hashText(text);

    // 将文本转换为有效的书签 ID 基础部分
    let base = text
      .toLowerCase()
      // 移除中文字符（用哈希值来区分）
      .replace(/[\u4e00-\u9fa5]/g, '')
      // 只保留字母和数字
      .replace(/[^a-z0-9]+/g, '_')
      // 移除首尾下划线
      .replace(/^_+|_+$/g, '');

    // 组合基础部分和哈希值
    let id = base ? `${base}_${hash}` : `bm_${hash}`;

    // 确保以字母开头
    if (!/^[a-z]/i.test(id)) {
      id = 'bm_' + id;
    }

    // 限制长度为 40 字符
    if (id.length > 40) {
      id = id.substring(0, 40);
    }

    // 缓存结果
    this.bookmarkIdCache.set(text, id);

    return id;
  }

  /**
   * 转换标题
   * 注意：不使用 HeadingLevel，因为 Word 内置的 Heading 样式有蓝色
   * 直接应用字体样式，与 markdown-viewer-extension 一致
   * 添加书签以支持内部锚点链接
   */
  private convertHeading(node: MdastNode): Paragraph {
    const depth = node.depth || 1;
    const headingKey = `h${depth}` as keyof typeof this.styles.headings;
    const headingStyle = this.styles.headings[headingKey];

    // 提取标题文本并生成书签 ID
    const headingText = this.extractTextFromNode(node);
    const bookmarkId = this.generateBookmarkId(headingText);

    // 创建带书签的标题内容
    const inlineContent = this.convertInlineNodes(node.children || [], {
      bold: headingStyle.bold,
      size: ptToHalfPt(headingStyle.fontSize),
      font: this.styles.baseFontConfig, // 中英文分离字体配置
    });

    return new Paragraph({
      // 不使用 heading 属性，避免应用 Word 内置的蓝色标题样式
      children: [
        new Bookmark({
          id: bookmarkId,
          children: inlineContent,
        }),
      ],
      spacing: {
        before: ptToTwips(headingStyle.spacingBefore),
        after: ptToTwips(headingStyle.spacingAfter),
        line: 360, // 1.5 倍行距
      },
      // H1 居中，其他左对齐
      alignment: depth === 1 ? AlignmentType.CENTER : AlignmentType.LEFT,
    });
  }

  /**
   * 转换段落
   */
  private convertParagraph(node: MdastNode): Paragraph {
    return new Paragraph({
      children: this.convertInlineNodes(node.children || []),
      spacing: {
        before: ptToTwips(this.styles.baseFontSize * 0.5),
        after: ptToTwips(this.styles.baseFontSize * 0.5),
        line: Math.round(this.styles.lineHeight * 240),
      },
    });
  }

  /**
   * 转换内联节点
   */
  private convertInlineNodes(
    nodes: MdastNode[],
    inheritedStyle: Partial<IRunOptions> = {}
  ): (TextRun | ExternalHyperlink | InternalHyperlink | Bookmark)[] {
    const runs: (TextRun | ExternalHyperlink | InternalHyperlink | Bookmark)[] =
      [];

    for (const node of nodes) {
      switch (node.type) {
        case 'text':
          runs.push(
            new TextRun({
              text: node.value || '',
              ...inheritedStyle,
            })
          );
          break;

        case 'strong':
          runs.push(
            ...this.convertInlineNodes(node.children || [], {
              ...inheritedStyle,
              bold: true,
            })
          );
          break;

        case 'emphasis':
          runs.push(
            ...this.convertInlineNodes(node.children || [], {
              ...inheritedStyle,
              italics: true,
            })
          );
          break;

        case 'delete':
          runs.push(
            ...this.convertInlineNodes(node.children || [], {
              ...inheritedStyle,
              strike: true,
            })
          );
          break;

        case 'inlineCode':
          runs.push(
            new TextRun({
              text: node.value || '',
              font: this.styles.code.fontFamily,
              size: ptToHalfPt(this.styles.code.fontSize),
              shading: {
                fill: this.styles.code.background,
                type: ShadingType.CLEAR,
              },
              ...inheritedStyle,
            })
          );
          break;

        case 'link': {
          const linkUrl = node.url || '';
          const linkChildren = this.convertInlineNodes(node.children || [], {
            ...inheritedStyle,
            color: '0563C1',
            underline: { type: 'single' },
          }) as TextRun[];

          // 处理 # 开头的内部锚点链接
          if (linkUrl.startsWith('#')) {
            const rawAnchor = linkUrl.slice(1); // 移除 # 前缀
            // 使用相同的转换逻辑生成书签 ID，确保与标题书签匹配
            const anchorId = this.generateBookmarkId(rawAnchor);
            runs.push(
              new InternalHyperlink({
                anchor: anchorId,
                children: linkChildren,
              })
            );
          } else {
            runs.push(
              new ExternalHyperlink({
                link: linkUrl,
                children: linkChildren,
              })
            );
          }
          break;
        }

        case 'break':
          runs.push(new TextRun({ break: 1 }));
          break;

        case 'html': {
          // 解析 HTML 锚点标签 <a id="xxx"></a> 或 <a name="xxx"></a>
          const html = node.value || '';
          const idMatch = html.match(
            /<a\s+(?:[^>]*?\s+)?id\s*=\s*["']([^"']+)["'][^>]*>/i
          );
          const nameMatch = html.match(
            /<a\s+(?:[^>]*?\s+)?name\s*=\s*["']([^"']+)["'][^>]*>/i
          );
          const anchorId = idMatch?.[1] || nameMatch?.[1];

          if (anchorId) {
            // 创建内联书签
            const bookmarkId = this.generateBookmarkId(anchorId);
            runs.push(
              new Bookmark({
                id: bookmarkId,
                children: [new TextRun('')], // 空内容，仅作为锚点
              })
            );
          }
          // 其他 HTML 标签忽略
          break;
        }

        default:
          // 递归处理子节点
          if (node.children) {
            runs.push(
              ...this.convertInlineNodes(node.children, inheritedStyle)
            );
          } else if (node.value) {
            runs.push(
              new TextRun({
                text: node.value,
                ...inheritedStyle,
              })
            );
          }
      }
    }

    return runs;
  }

  /**
   * 转换代码块
   */
  private convertCodeBlock(node: MdastNode): Paragraph {
    const codeStyle = this.styles.code;
    const lines = (node.value || '').split('\n');

    const children: TextRun[] = [];
    lines.forEach((line, index) => {
      children.push(
        new TextRun({
          text: line,
          font: codeStyle.fontFamily,
          size: ptToHalfPt(codeStyle.fontSize),
        })
      );
      if (index < lines.length - 1) {
        children.push(new TextRun({ break: 1 }));
      }
    });

    return new Paragraph({
      children,
      shading: {
        fill: codeStyle.background,
        type: ShadingType.CLEAR,
      },
      spacing: {
        before: ptToTwips(10),
        after: ptToTwips(10),
        line: 276, // 约 1.15 倍行距
      },
      indent: {
        left: convertInchesToTwip(0.2),
        right: convertInchesToTwip(0.2),
      },
      border: {
        top: {
          color: codeStyle.borderColor,
          space: 10,
          style: BorderStyle.SINGLE,
          size: 6,
        },
        bottom: {
          color: codeStyle.borderColor,
          space: 10,
          style: BorderStyle.SINGLE,
          size: 6,
        },
        left: {
          color: codeStyle.borderColor,
          space: 10,
          style: BorderStyle.SINGLE,
          size: 6,
        },
        right: {
          color: codeStyle.borderColor,
          space: 10,
          style: BorderStyle.SINGLE,
          size: 6,
        },
      },
    });
  }

  /**
   * 转换引用块
   */
  private async convertBlockquote(node: MdastNode): Promise<Table> {
    const bqStyle = this.styles.blockquote;
    const cellChildren: (Paragraph | Table)[] = [];

    if (node.children) {
      for (const child of node.children) {
        const converted = await this.convertNode(child);
        if (converted) {
          if (Array.isArray(converted)) {
            cellChildren.push(...converted);
          } else {
            cellChildren.push(converted);
          }
        }
      }
    }

    // 使用表格模拟引用块的左边框效果
    const cell = new TableCell({
      children:
        cellChildren.length > 0
          ? cellChildren
          : [new Paragraph({ children: [] })],
      margins: {
        top: 80,
        bottom: 80,
        left: ptToTwips(bqStyle.paddingLeft),
        right: 100,
      },
      borders: {
        top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        left: {
          style: BorderStyle.SINGLE,
          size: ptToEighths(bqStyle.borderWidth),
          color: bqStyle.borderColor,
        },
      },
    });

    return new Table({
      rows: [new TableRow({ children: [cell] })],
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.FIXED,
    });
  }

  /**
   * 转换列表
   */
  private async convertList(
    node: MdastNode,
    level: number = 0
  ): Promise<Paragraph[]> {
    const paragraphs: Paragraph[] = [];
    const ordered = node.ordered || false;
    let itemNumber = node.start || 1;

    for (const item of node.children || []) {
      const itemParagraphs = await this.convertListItem(
        item,
        level,
        ordered,
        itemNumber
      );
      paragraphs.push(...itemParagraphs);
      if (ordered) itemNumber++;
    }

    return paragraphs;
  }

  /**
   * 转换列表项
   */
  private async convertListItem(
    node: MdastNode,
    level: number = 0,
    ordered: boolean = false,
    itemNumber: number = 1
  ): Promise<Paragraph[]> {
    const paragraphs: Paragraph[] = [];
    const indent = convertInchesToTwip(0.25 * (level + 1));
    const bullet = ordered ? `${itemNumber}.` : '•';

    for (let i = 0; i < (node.children || []).length; i++) {
      const child = node.children![i];

      if (child.type === 'paragraph') {
        const inlineRuns = this.convertInlineNodes(child.children || []);

        // 只在第一个段落前添加项目符号
        if (i === 0) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${bullet} ` }),
                ...(inlineRuns as TextRun[]),
              ],
              indent: { left: indent },
              spacing: {
                before: ptToTwips(2),
                after: ptToTwips(2),
                line: Math.round(this.styles.lineHeight * 240),
              },
            })
          );
        } else {
          paragraphs.push(
            new Paragraph({
              children: inlineRuns as TextRun[],
              indent: { left: indent },
              spacing: {
                before: ptToTwips(2),
                after: ptToTwips(2),
                line: Math.round(this.styles.lineHeight * 240),
              },
            })
          );
        }
      } else if (child.type === 'list') {
        // 嵌套列表
        const nestedItems = await this.convertList(child, level + 1);
        paragraphs.push(...nestedItems);
      } else {
        const converted = await this.convertNode(child);
        if (converted) {
          if (Array.isArray(converted)) {
            paragraphs.push(...(converted as Paragraph[]));
          } else {
            paragraphs.push(converted as Paragraph);
          }
        }
      }
    }

    return paragraphs;
  }

  /**
   * 转换表格
   */
  private convertTable(node: MdastNode): Table {
    const tableStyle = this.styles.table;
    const alignments = node.align || [];
    const rows: TableRow[] = [];

    // 遍历表格行
    (node.children || []).forEach((rowNode, rowIndex) => {
      if (rowNode.type === 'tableRow') {
        const isHeaderRow = rowIndex === 0;
        const cells: TableCell[] = [];

        // 遍历单元格
        (rowNode.children || []).forEach((cellNode, colIndex) => {
          if (cellNode.type === 'tableCell') {
            const cellConfig = this.createTableCell(
              cellNode,
              isHeaderRow,
              rowIndex,
              colIndex,
              alignments,
              tableStyle
            );
            cells.push(new TableCell(cellConfig));
          }
        });

        rows.push(new TableRow({ children: cells }));
      }
    });

    return new Table({
      rows,
      layout: TableLayoutType.AUTOFIT,
      alignment: AlignmentType.CENTER,
      // 不设置 width，让表格根据内容自动调整宽度（与 markdown-viewer-extension 一致）
    });
  }

  /**
   * 创建表格单元格
   * 与 markdown-viewer-extension 一致：字体 10pt (size: 20)
   */
  private createTableCell(
    cellNode: MdastNode,
    isHeaderRow: boolean,
    rowIndex: number,
    colIndex: number,
    alignments: (string | null)[],
    tableStyle: DocxStyles['table']
  ): ITableCellOptions {
    // 确定对齐方式
    const cellAlignment = alignments[colIndex];
    let paragraphAlignment: (typeof AlignmentType)[keyof typeof AlignmentType] =
      AlignmentType.LEFT;
    if (isHeaderRow) {
      paragraphAlignment = AlignmentType.CENTER;
    } else if (cellAlignment === 'center') {
      paragraphAlignment = AlignmentType.CENTER;
    } else if (cellAlignment === 'right') {
      paragraphAlignment = AlignmentType.RIGHT;
    }

    // 确定背景色 (斑马条纹)
    let shading: ITableCellOptions['shading'];
    if (isHeaderRow) {
      shading = {
        fill: tableStyle.headerBackground,
        type: ShadingType.CLEAR,
      };
    } else if (tableStyle.zebra.enabled) {
      // 数据行斑马条纹 (rowIndex 从 0 开始，0 是表头)
      const isOddDataRow = (rowIndex - 1) % 2 === 0;
      const background = isOddDataRow
        ? tableStyle.zebra.oddBackground
        : tableStyle.zebra.evenBackground;
      if (background !== 'FFFFFF') {
        shading = { fill: background, type: ShadingType.CLEAR };
      }
    }

    // 边框样式
    const borderConfig = {
      style: BorderStyle.SINGLE,
      size: ptToEighths(tableStyle.borderWidth),
      color: tableStyle.borderColor,
    };

    // 内联内容 - 使用 10pt 字体 (size: 20 half-points)，与 markdown-viewer-extension 一致
    // 使用完整字体配置支持中英文分离（西文 Times New Roman，中文 SimSun 宋体）
    const inlineRuns = this.convertInlineNodes(cellNode.children || [], {
      bold: isHeaderRow && tableStyle.headerBold,
      size: 20, // 10pt = 20 half-points，与 markdown-viewer-extension 表格字体大小一致
      font: this.styles.baseFontConfig, // 中英文分离字体配置
    });

    const paragraphOptions: IParagraphOptions = {
      children: inlineRuns as TextRun[],
      alignment: paragraphAlignment,
      // 与 markdown-viewer-extension 一致的单元格内段落间距
      spacing: { before: 60, after: 60, line: 240 },
    };

    return {
      children: [new Paragraph(paragraphOptions)],
      borders: {
        top: borderConfig,
        bottom: borderConfig,
        left: borderConfig,
        right: borderConfig,
      },
      shading,
      margins: {
        top: 159, // 固定 0.28cm 内边距
        bottom: 159,
        left: 159,
        right: 159,
      },
    };
  }

  /**
   * 转换分割线
   * 当 treatHrAsPageBreak 为 true 时，插入分页符
   * 当 treatHrAsPageBreak 为 false 时，忽略分割线（不产生任何输出）
   */
  private convertThematicBreak(): Paragraph | null {
    if (this.exportOptions.treatHrAsPageBreak) {
      // 使用 PageBreak 元素插入分页符
      return new Paragraph({
        children: [new PageBreak()],
      });
    } else {
      // 不点亮时，完全忽略分割线
      return null;
    }
  }
}

/**
 * 导出配置选项
 */
export interface ExportOptions {
  /** 是否将水平分割线作为分页处理，默认为 true */
  treatHrAsPageBreak?: boolean;
  /** 页面方向：portrait (纵向) 或 landscape (横向)，默认为 portrait */
  orientation?: 'portrait' | 'landscape';
}

/**
 * 便捷导出函数
 */
export async function exportMarkdownToDocx(
  markdown: string,
  styles?: Partial<DocxStyles>,
  options?: ExportOptions
): Promise<Blob> {
  const exporter = new DocxExporter(styles);
  return exporter.exportToBlob(markdown, options);
}
